import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { IonRouterOutlet, ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { MatchService } from '../../_core/services/match.service';
import { TieService } from '../../_core/services/tie.service';
import { Tie } from '../../_shared/models/tie.model';
import { User } from '../../_shared/models/user.model';
import { CreateTieComponent } from '../create-tie/create-tie.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
    selector: 'app-tie-list',
    templateUrl: './tie-list.page.html',
    styleUrls: ['./tie-list.page.scss'],
})
export class TieListPage implements OnInit, OnDestroy {
    private translateService = inject(TranslateService);
    
    ties: Tie[] = [];
    isLoading = false;
    getMoreTies = false;
    page = 0;

    private destroyed$: Subject<void> = new Subject();

    tie_placeholder = this.translateService.instant('Track scores & stats for your team matches and share in real-time with your fans!\n\nClick on the plus button below to get started!\n\nIf you would like to have live score for your league or to have unique link for all team matches contact us via feedback@ionsport.com or send us a message through the feedback form.');

    constructor(
        private tieService: TieService,
        private modalController: ModalController,
        private routerOutlet: IonRouterOutlet,
        private auth: AuthService,
        private matchService: MatchService
    ) { }

    async ngOnInit() {
        this.isLoading = true;
        this.getTies();
        this.subscriptions();
        
        // Subscribe to language changes to update placeholder text
        this.translateService.onLangChange.pipe(
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.tie_placeholder = this.translateService.instant('Track scores & stats for your team matches and share in real-time with your fans!\n\nClick on the plus button below to get started!\n\nIf you would like to have live score for your league or to have unique link for all team matches contact us via feedback@ionsport.com or send us a message through the feedback form.');
        });
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    doRefresh(refresher) {
        this.isLoading = true;
        this.page = 0;
        this.ties = [];
        this.getTies(refresher);
    }

    doInfinite(infiniteScroll) {
        this.page++;
        this.getTies();
        setTimeout(() => {
            infiniteScroll.target.complete();
        }, 1500);
    }

    getTies(refresher?) {
        const paginationConfig = {
            page: this.page,
            sort: -1,
            limit: 7,
            type: 'ties'
        };

        this.tieService
            .getMyTies(paginationConfig)
            .pipe(finalize(() => {
                this.isLoading = false;
                if (refresher) refresher.detail.complete();
            }))
            .subscribe(res => {
                res.data.forEach(tie => {
                    this.ties.push(tie);
                });

                if (res.data.length < 7) {
                    this.getMoreTies = false;
                } else {
                    this.getMoreTies = true;
                }
            });
    }

    async createTie() {
        const modal = await this.modalController.create({
            component: CreateTieComponent,
            id: 'create-tie',
            presentingElement: this.routerOutlet.nativeEl,
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();

        if (data) {
            this.ties.unshift(data);
        }
    }

    private subscriptions() {
        this.tieService.subscribeDelete().subscribe(
            tieId => {
                this.removeTie(tieId);
            }
        );

        this.tieService.subscribeEdit().subscribe(
            updatedTie => {
                this.ties.forEach((tie, i) => {
                    if (tie._id === updatedTie._id) {
                        this.ties[i] = updatedTie;
                    }
                });
            }
        );


        this.matchService
            .subscribeAddEventRequest()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                type => {
                    if (type === 'tie') {
                        this.createTie();
                    }
                }
            );
    }

    removeTie(tieId: string) {
        this.ties.forEach((tie, i) => {
            if (tie._id === tieId) {
                this.ties.splice(i, 1);
            }
        });
    }

}
